import React from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { parsePhoneNumbers, telHref, formatPhone } from '../../utils/phoneNumbers';

/* [CF 2026-08-17 #7 / V4] One call control for the whole site.
 *
 * Before this every page built its own `tel:` link straight from the single
 * `mobileNumber` column, so a business with several numbers only ever offered
 * the first one. This takes the whole business record, reads every number off it
 * (see utils/phoneNumbers.js) and:
 *
 *   - one number  -> behaves exactly as before, a plain tel: link
 *   - several     -> opens a small chooser listing all of them
 *   - none        -> renders the icon greyed out and does nothing, instead of a
 *                    dead `tel:undefined` link
 *
 * Drop-in for the old markup: keep the same children and className, swap the
 * `href` for `record`.
 */
export default function CallLink({
  record,
  children,
  className,
  title,
  onCall,
  /* Pulled out of `...rest` ON PURPOSE. Several call sites pass an onClick to
     record a CONTACT event. If that landed in `rest` it would be spread over
     this component's own handler and the number chooser would never open. It is
     captured here and invoked alongside our behaviour instead. */
  onClick: onClickFromCaller,
  style,
  ...rest
}) {
  const { t } = useTranslation();
  const numbers = parsePhoneNumbers(record);
  const track = (e) => {
    if (onCall) onCall(e);
    if (onClickFromCaller) onClickFromCaller(e);
  };

  // Nothing to call — show the control but make it inert rather than linking to
  // "tel:undefined", which is what produced the literal "Call#" the client saw.
  if (!numbers.length) {
    return (
      <a
        className={className}
        title={title}
        aria-disabled="true"
        style={{ ...(style || {}), opacity: 0.45, cursor: 'default' }}
        href="#!"
        {...rest}
        onClick={(e) => e.preventDefault()}
      >
        {children}
      </a>
    );
  }

  if (numbers.length === 1) {
    return (
      <a
        className={className}
        title={title}
        style={style}
        href={telHref(numbers[0])}
        {...rest}
        onClick={track}
      >
        {children}
      </a>
    );
  }

  const choose = (e) => {
    e.preventDefault();
    track(e);

    const rows = numbers
      .map(
        (p) =>
          `<a href="${telHref(p)}" class="cav-call-option" dir="ltr">${formatPhone(p)}</a>`
      )
      .join('');

    Swal.fire({
      title: t('Call'),
      html: `<div class="cav-call-list">${rows}</div>`,
      showCancelButton: true, // also keeps this a real dialog, not a toast
      showConfirmButton: false,
      cancelButtonText: t('Cancel'),
      // A function here stops the shared Swal patch downgrading this to a toast.
      didOpen: (el) => {
        el.querySelectorAll('.cav-call-option').forEach((a) =>
          a.addEventListener('click', () => Swal.close())
        );
      },
    });
  };

  return (
    <a
      className={className}
      title={title}
      style={style}
      href="#!"
      {...rest}
      onClick={choose}
    >
      {children}
    </a>
  );
}
